// StickiesTable.js - Updated with hierarchical tag system
import React, { useMemo, useCallback, useState, useEffect } from 'react'; 
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';
import TagDropdown from './TagDropdown';
import { fetchTagDomains } from '../../api/stickiesApi'; // Import the API function
import './StickiesListPanelMain.css';
import './TagDropdown.css';

const StickiesTable = ({ 
  stickies, 
  selectedStickies,
  displayModes,
  onStickyClick,
  onStickySelect,
  onReorderSticky,
  getStickyIcon,
  onTitleUpdate,
  onTagUpdate,
  showContent = true
}) => {
  const columnHelper = createColumnHelper();
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [tagDomains, setTagDomains] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch tag domains on component mount
  useEffect(() => {
    const loadTagDomains = async () => {
      try {
        const domains = await fetchTagDomains();
        setTagDomains(domains);
      } catch (error) {
        console.error('Error loading tag domains:', error);
        // Fallback to default domains
        setTagDomains({
          "Philosophy": ["thesis", "argument", "analysis"],
          "Programming": ["bug", "feature", "refactor"], 
          "General": ["research", "todo", "important"]
        });
      } finally {
        setLoading(false);
      }
    };

    loadTagDomains();
  }, []);

  // Handle domain selection (tag1)
  const handleDomainChange = useCallback((stickyId, domain) => {
   /// console.log(`Domain changed for sticky ${stickyId}: ${domain}`);
    // Clear tag2 when domain changes
    onTagUpdate(stickyId, 0, domain); // tag1 = domain
    onTagUpdate(stickyId, 1, '');     // tag2 = clear
  }, [onTagUpdate]);

  // Handle specific tag selection (tag2)
  const handleTagChange = useCallback((stickyId, tag) => {
   /// console.log(`Tag changed for sticky ${stickyId}: ${tag}`);
    onTagUpdate(stickyId, 1, tag); // tag2 = specific tag
  }, [onTagUpdate]);

  // Get available tags for a specific domain
  const getTagsForDomain = useCallback((domain) => {
    return domain ? tagDomains[domain] || [] : [];
  }, [tagDomains]);

  // Get domain options
  const getDomainOptions = useMemo(() => {
    return Object.keys(tagDomains);
  }, [tagDomains]);

  // Memoize columns to prevent unnecessary re-renders
// Memoize columns to prevent unnecessary re-renders
const columns = useMemo(() => [
  // 1. CHECKBOX FIRST
  columnHelper.accessor('id', {
    id: 'select',
    header: '',
    cell: ({ row }) => (
      <input 
        type="checkbox"
        checked={Boolean(selectedStickies[row.original.id])} // Use object syntax instead of Set
        onChange={(e) => onStickySelect(row.original.id, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
    ),
    size: 25,
  }),
  
  // 2. NUMBER SECOND  
  columnHelper.accessor('list_order', {
    id: 'number',
    header: '#',
    cell: ({ row }) => (
      <div className="sticky-number-column">
        {row.index + 1}
      </div>
    ),
    size: 30,
  }),
  
  // 3. REORDER THIRD
  columnHelper.accessor('list_order', {
    id: 'reorder',
    header: '',
    cell: ({ row, table }) => (
      <div className="reorder-controls">
        <button 
          className="reorder-btn up"
          onClick={(e) => {
            e.stopPropagation();
            onReorderSticky(row.original.id, 'up');
          }}
          disabled={row.index === 0}
          title="Move up"
        >
          ▲
        </button>
        <button 
          className="reorder-btn down"
          onClick={(e) => {
            e.stopPropagation();
            onReorderSticky(row.original.id, 'down');
          }}
          disabled={row.index === table.getRowModel().rows.length - 1}
          title="Move down"
        >
          ▼
        </button>
      </div>
    ),
    size: 40,
  }),
  
  // 4. TYPE ICON
  columnHelper.accessor('type', {
    id: 'type-icon',
    header: '',
    cell: ({ row }) => (
      <div className="sticky-type-icon">
        {getStickyIcon(row.original.type)}
      </div>
    ),
    size: 25,
  }),
  
  // 5. VISIBILITY ICON
  columnHelper.accessor('display_mode', {
    id: 'visibility-icon',
    header: '',
    cell: ({ row }) => (
      <div className="sticky-visibility-icon">
        {displayModes[row.original.id] === 'list' ? '📍' : '👁️'}
      </div>
    ),
    size: 25,
  }),
  
  // 6. TITLE
  columnHelper.accessor('title', {
    id: 'title',
    header: 'Title',
    cell: ({ getValue, row }) => {
      const isEditing = editingTitle === row.original.id;
      
      return isEditing ? (
        <input
          type="text"
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => {
            onTitleUpdate?.(row.original.id, editingValue);
            setEditingTitle(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onTitleUpdate?.(row.original.id, editingValue);
              setEditingTitle(null);
            }
            if (e.key === 'Escape') {
              setEditingTitle(null);
              setEditingValue(getValue() || '');
            }
          }}
          autoFocus
          onClick={(e) => e.stopPropagation()}
          className="table-title-input"
        />
      ) : (
        <div 
          className="sticky-title-main"
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditingTitle(row.original.id);
            setEditingValue(getValue() || '');
          }}
          onClick={(e) => {
            e.stopPropagation();
            onStickyClick(row.original);
          }}
          title="Click to open note, double-click to edit title"
        >
          {getValue() || 'Untitled'}
        </div>
      );
    },
    size: 120,
  }),
  
  // 7. CONTENT (CONDITIONAL)
  ...(showContent ? [
    columnHelper.accessor('content', {
      id: 'content',
      header: 'Content',
      cell: ({ getValue }) => (
        <div className="content-text">
          {getValue()?.substring(0, 150) || 'Empty note...'}
        </div>
      ),
      size: 280,
    })
  ] : []),
  
  // 8. DOMAIN TAG
  columnHelper.accessor('tag1', {
    id: 'tag1',
    header: 'Domain',
    cell: ({ getValue, row }) => {
      return (
        <TagDropdown
          value={getValue() || ''}
          onChange={(domain) => handleDomainChange(row.original.id, domain)}
          options={getDomainOptions}
          placeholder="Select domain"
          type="domain"
          disabled={loading}
        />
      );
    },
    size: 80,
  }),
  
  // 9. SPECIFIC TAG
  columnHelper.accessor('tag2', {
    id: 'tag2', 
    header: 'Tag',
    cell: ({ getValue, row }) => (
      <TagDropdown
        value={getValue() || ''}
        onChange={(tag) => handleTagChange(row.original.id, tag)}
        options={getTagsForDomain(row.original.tag1)}
        placeholder="Select tag"
        type="tag"
        disabled={!row.original.tag1 || loading}
      />
    ),
    size: 80,
  }),
  
  // 10. DATE
  columnHelper.accessor('created_at', {
    id: 'date',
    header: 'Date',
    cell: ({ getValue }) => (
      <div className="sticky-type-main">
        {getValue() ? new Date(getValue()).toLocaleDateString() : 'No date'}
      </div>
    ),
    size: 100,
  }),
], [
  // dependencies stay the same
  selectedStickies, 
  displayModes, 
  onStickySelect, 
  onReorderSticky, 
  getStickyIcon, 
  editingTitle, 
  editingValue, 
  onTitleUpdate,
  handleDomainChange,
  handleTagChange,
  getDomainOptions,
  getTagsForDomain,
  loading,
  showContent
]);

  // Table instance with stable data reference
  const table = useReactTable({
    data: stickies,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    autoResetAll: false,
  });

  if (loading) {
    return (
      <div className="stickies-list-main">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading tag domains...
        </div>
      </div>
    );
  }

  return (
    <div className="stickies-list-main">
      <table className="stickies-table">
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr 
              key={row.original.id}
              className="sticky-list-item-main"
              onClick={(e) => {
                // Only trigger row click if it's not coming from interactive elements
                if (!e.target.closest('.sticky-title-main, .tag-dropdown-container')) {
                  onStickyClick(row.original);
                }
              }}
              onDoubleClick={(e) => e.preventDefault()}
            >
              {row.getVisibleCells().map(cell => (
                <td 
                  key={cell.id} 
                  className="sticky-cell"
                  style={{ width: `${cell.column.getSize()}px` }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default React.memo(StickiesTable);